/**
 * Clipboard utilities with Safari fallback support
 */

import { detectBrowser } from './browserCompatibility';

/**
 * Copy text to clipboard with Safari fallback support
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  const browser = detectBrowser();
  
  // Safari requires user gesture for clipboard access
  // In notification contexts, we need to use the fallback method
  if (browser.isSafari) {
    return await fallbackCopyToClipboard(text);
  }
  
  try {
    // Try modern clipboard API first for non-Safari browsers
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
    
    // Fallback for other browsers that don't support modern API
    return await fallbackCopyToClipboard(text);
  } catch (error) {
    console.error('Clipboard API failed, trying fallback:', error);
    
    // Try fallback method
    try {
      return await fallbackCopyToClipboard(text);
    } catch (fallbackError) {
      console.error('Fallback clipboard method also failed:', fallbackError);
      return false;
    }
  }
}

/**
 * Fallback clipboard method using document.execCommand
 */
function fallbackCopyToClipboard(text: string): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      // Make the textarea invisible
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      textArea.style.color = 'transparent';
      
      document.body.appendChild(textArea);
      
      // Select and copy the text
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      resolve(successful);
    } catch (error) {
      console.error('Fallback copy failed:', error);
      resolve(false);
    }
  });
}

/**
 * Check if clipboard API is supported
 */
export function isClipboardSupported(): boolean {
  const browser = detectBrowser();
  
  // Safari has limited clipboard support in certain contexts
  if (browser.isSafari) {
    // Safari requires user gesture for clipboard access
    return navigator.clipboard !== undefined && 
           typeof navigator.clipboard.writeText === 'function';
  }
  
  return navigator.clipboard !== undefined && 
         typeof navigator.clipboard.writeText === 'function';
}

/**
 * Enhanced copy function with better error handling and user feedback
 */
export async function enhancedCopyToClipboard(
  text: string, 
  onSuccess?: () => void, 
  onError?: (error: Error) => void
): Promise<boolean> {
  try {
    const success = await copyToClipboard(text);
    
    if (success) {
      onSuccess?.();
      return true;
    } else {
      const error = new Error('Failed to copy to clipboard');
      onError?.(error);
      return false;
    }
  } catch (error) {
    console.error('Enhanced copy failed:', error);
    onError?.(error as Error);
    return false;
  }
}